// auditpm-v6
import React, { useState, useEffect } from 'react'
import { useAuth } from '../lib/AuthContext'
import { supabase } from '../lib/supabase'
import { toast, failed } from '../lib/toast'

// Default email templates - used as fallback if nothing saved
export const DEFAULT_TEMPLATES = {
  enquiry: {
    subject: 'bizSAFE Audit Application — {FIRM_NAME}',
    body: `Dear {CLIENT_NAME},

Thank you for your interest in our {AUDIT_TYPE} service.

Please complete your application form here:
{FORM_LINK}

Once submitted, we will prepare a quotation for your review within 1–2 business days.

Best regards
{FIRM_NAME}`,
  },
  quotation: {
    subject: '{COMPANY_NAME} - bizSAFE WSH RM Audit {QUOTE_REF}',
    body: `Dear {CLIENT_NAME},

Thank you for submitting the online questionnaire.

Please view and accept our bizSAFE WSH RM Audit quotation here:
{SIGN_LINK}

This quotation is valid for {VALIDITY_DAYS} days.

Feel-free to contact us should you require any clarifications, thank you.

Best regards,
{FIRM_NAME}
(65) 6376 0550`,
  },
  auditPlanClient: {
    subject: 'Audit Plan — {CLIENT_NAME} — {AUDIT_DATE}',
    body: `Dear {CLIENT_NAME},

Please acknowledge receipt of this Audit Plan by clicking the link below:
{ACK_LINK}

Audit Date & Time: {AUDIT_DATE}, {AUDIT_TIME}
Audit Location: {AUDIT_SITE}

Kindly ensure the following preparations are made in advance:
• NEW: Have at least 1 programme to improve health of workers in general (e.g., through health screening and health coaching, healthy diet, active lifestyle, smoking cessation, weight management and stress management)
• NEW: Must include mental well-being RA, personal health-risk RA: Refer to Annex H to J for examples
• NEW: If you have full-time drivers, ensure that a Fatigue Management Plan is in place
• NEW: Ensure RM Procedure is updated to the latest Code of Practice: {COP_LINK}
• The following people will need to be present:
   - RA Approver
   - RM/RA Team Leader
   - RM/RA Members
   - Implementation Person for Additional Control Measures
   - At least 3 additional workers/equivalent (if company has less than 3 workers, we will declare accordingly on the audit report)
   - At least 1 supervisor/equivalent (if company does not have a supervisor, we will declare accordingly on the audit report)

Thank you.
{FIRM_NAME}`,
  },
  auditPlanAuditor: {
    subject: 'Audit Assignment — {CLIENT_NAME} — {AUDIT_DATE}',
    body: `Dear {AUDITOR_NAME},

You have been assigned to conduct the following audit:

Client: {CLIENT_NAME}
Audit Type: {AUDIT_TYPE}
Date: {AUDIT_DATE}
Time: {AUDIT_TIME}
Site: {AUDIT_SITE}

Please complete your Declaration of Impartiality before the audit date:
{COI_LINK}

Best regards
{FIRM_NAME}`,
  },
  reportClient: {
    subject: 'Your audit report is ready for endorsement — {CLIENT_NAME}',
    body: `Dear {CLIENT_NAME},

Your audit report is ready:
{REPORT_LINK}

Please access the above link and complete three quick steps:

1. Review the report for accuracy
2. Fill in your company details in the required fields
3. Management Representative to sign off on the last page of the report

Once received, we will proceed with the online application for bizSAFE on your behalf.

Thank you.
{FIRM_NAME}`,
  },
  // New WSHC client-portal process (from 2 Nov 2026): no application form; the client applies on WSHC themselves.
  // Filled in by the job page: {REPORT_KIND} audit report / renewal audit report; {SIGN_NOW} (renewal before WSHC's
  // window: "sign now, don't wait"); {AFTER_SIGN} = what happens after signing (sits directly under Step 3).
  // The report link stays ALONE on its own line, nothing after it — Outlook then keeps it clickable.
  reportClientPortal: {
    subject: '{COMPANY} - Please acknowledge your bizSAFE {REPORT_KIND}',
    body: `Dear {CLIENT_NAME},

Your bizSAFE {REPORT_KIND} is ready.{SIGN_NOW}

From 2 November 2026, companies must apply for bizSAFE themselves on WSHC's new portal. CCIS can no longer apply for you.

Your next steps
Step 1: Open your report here:
{REPORT_LINK}
Step 2: Check the report.
Step 3: Scroll to the bottom of the page and enter the name and email of the staff who will apply for bizSAFE for your company (he/she will need your company's CorpPass).
Step 4: Tick the confirmation box, enter your name and designation, then click "Submit acknowledgement". {AFTER_SIGN}

If you are not the right person to acknowledge this report, please forward this email to your Management Representative.

Questions? Call us at (65) 6376 0550.

Regards,
{FIRM_NAME}`,
  },
  reportReturned: {
    subject: 'Audit report returned for revision — {CLIENT_NAME}',
    body: `Dear {AUDITOR_NAME},

The audit report for {CLIENT_NAME} has been returned for revision and requires your action.

Reason: {RETURN_REASON}

Please revise the package and resubmit from the Summary step:
{AUDIT_LINK}

{FIRM_NAME}`,
  },
  outstandingItems: {
    subject: 'Outstanding items for your bizSAFE audit — {CLIENT_NAME}',
    body: `Dear {CLIENT_NAME},

Please find below the list of outstanding items to be submitted to us within 3 working days.

{ITEMS}

Thank you.`,
  },
  wshcSubmitted: {
    subject: 'bizSAFE application submitted — {CLIENT_NAME}',
    body: `Dear {CLIENT_NAME},

We've submitted the online bizSAFE application on your behalf.

Please take note of the Guidance Notes (https://www.tal.sg/wshc/-/media/TAL/Wshc/Programmes/Files/Guidance-Notes-for-bizSAFE-Enterprise-Application.pdf) and the TAL External Privacy Policy (https://www.tal.sg/privacy-statement), and inform us of any disputes within 3 working days.

You should receive the E-Certificate from WSHC within 1-10 working days. All approval or rejection emails will be sent directly to the Senior Management email address you provided.

If the E-Certificate does not arrive after 10 working days, please request it from WSHC here: https://survey.wshc.sg/Community/se/705E3EE811E215C7

Thank you.
{FIRM_NAME}`,
  },
  renewalReminder: {
    subject: 'bizSAFE Renewal Reminder — {CLIENT_NAME}',
    body: `Dear {CLIENT_NAME},

Your bizSAFE certification is expiring on {EXPIRY_DATE}.

To proceed with your renewal audit, please fill in the renewal application form:
{RENEWAL_LINK}

Your company details have been pre-filled — you only need to update any information that has changed.

Best regards
{FIRM_NAME}`,
  },
  trainingEvaluation: {
    subject: 'Training Evaluation — {TRAINING_CONTENT}',
    body: `Dear {AUDITOR_NAME},

A training record has been added for you in AuditPM:

Training: {TRAINING_CONTENT}
Date: {TRAINING_DATE}

Please complete the Training Evaluation form here:
{EVAL_LINK}

Thank you.
{FIRM_NAME}`,
  },
}

export function getTemplate(key) {
  try {
    const saved = localStorage.getItem(`email_template_${key}`)
    if (saved) return JSON.parse(saved)
  } catch (e) {}
  return DEFAULT_TEMPLATES[key]
}

export function applyTemplate(template, vars) {
  let subject = template.subject
  let body = template.body
  Object.entries(vars).forEach(([k, v]) => {
    const token = `{${k}}`
    subject = subject.split(token).join(v || '')
    body = body.split(token).join(v || '')
  })
  return { subject, body }
}

const TEMPLATE_LABELS = {
  enquiry: { label: 'Enquiry questionnaire email', desc: 'Sent when you click "Send enquiry form" on the Dashboard' },
  quotation: { label: 'Quotation email', desc: 'Sent when you click "Save & send to client" on a quotation' },
  auditPlanClient: { label: 'Audit plan — client email', desc: 'Sent to client when you click "Send audit plan" on a job' },
  auditPlanAuditor: { label: 'Audit plan — auditor email', desc: 'Sent to auditor(s) when you click "Send audit plan" on a job' },
  reportClient: { label: 'Audit report — client endorsement email', desc: 'Drafted in Outlook when you approve a report and send it to the client to endorse' },
  reportClientPortal: { label: 'Audit report — client endorsement email (new WSHC portal)', desc: 'Drafted in Outlook when you send a bizSAFE report on the new WSHC-portal process (the client applies on WSHC themselves)' },
  reportReturned: { label: 'Audit report — returned to auditor email', desc: 'Auto-sent to the assigned auditor(s) when a report is returned for revision (by office or WSHC)' },
  outstandingItems: { label: 'Outstanding items — client email', desc: 'Drafted in Outlook when the auditor clicks "Email list of outstanding items to client" (Findings step)' },
  wshcSubmitted: { label: 'bizSAFE application submitted — client email', desc: 'Drafted in Outlook when you click "Record Submission to WSHC"' },
  renewalReminder: { label: 'Renewal reminder email', desc: 'Auto-sent by the system 6 and 3 months before bizSAFE expiry' },
  trainingEvaluation: { label: 'Training evaluation email', desc: 'Auto-sent to the auditor when a new training record is added under Personnel → Training' },
}

const VARIABLE_HINTS = {
  enquiry: ['{CLIENT_NAME}', '{AUDIT_TYPE}', '{FORM_LINK}', '{FIRM_NAME}'],
  quotation: ['{CLIENT_NAME}', '{COMPANY_NAME}', '{QUOTE_REF}', '{AUDIT_TYPE}', '{SIGN_LINK}', '{VALIDITY_DAYS}', '{FIRM_NAME}'],
  auditPlanClient: ['{CLIENT_NAME}', '{AUDIT_DATE}', '{AUDIT_TIME}', '{AUDIT_SITE}', '{ACK_LINK}', '{COP_LINK}', '{FIRM_NAME}'],
  auditPlanAuditor: ['{AUDITOR_NAME}', '{CLIENT_NAME}', '{AUDIT_TYPE}', '{AUDIT_DATE}', '{AUDIT_TIME}', '{AUDIT_SITE}', '{COI_LINK}', '{FIRM_NAME}'],
  reportClient: ['{CLIENT_NAME}', '{REPORT_LINK}', '{FIRM_NAME}'],
  reportClientPortal: ['{CLIENT_NAME}', '{COMPANY}', '{REPORT_KIND}', '{SIGN_NOW}', '{REPORT_LINK}', '{AFTER_SIGN}', '{FIRM_NAME}'],
  reportReturned: ['{AUDITOR_NAME}', '{CLIENT_NAME}', '{RETURN_REASON}', '{AUDIT_LINK}', '{FIRM_NAME}'],
  outstandingItems: ['{CLIENT_NAME}', '{ITEMS}'],
  wshcSubmitted: ['{CLIENT_NAME}', '{FIRM_NAME}'],
  renewalReminder: ['{CLIENT_NAME}', '{EXPIRY_DATE}', '{RENEWAL_LINK}', '{FIRM_NAME}'],
  trainingEvaluation: ['{AUDITOR_NAME}', '{TRAINING_CONTENT}', '{TRAINING_DATE}', '{EVAL_LINK}', '{FIRM_NAME}'],
}

function TemplateEditor({ templateKey, onSaved }) {
  const saved = getTemplate(templateKey)
  const [subject, setSubject] = useState(saved.subject)
  const [body, setBody] = useState(saved.body)
  const [savedMsg, setSavedMsg] = useState(false)

  function save() {
    localStorage.setItem(`email_template_${templateKey}`, JSON.stringify({ subject, body }))
    setSavedMsg(true)
    setTimeout(() => setSavedMsg(false), 2000)
    onSaved?.()
  }

  function reset() {
    const def = DEFAULT_TEMPLATES[templateKey]
    setSubject(def.subject)
    setBody(def.body)
  }

  const hints = VARIABLE_HINTS[templateKey] || []

  return (
    <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16, marginTop: 4 }}>
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 6 }}>
          Available variables — click to insert:
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {hints.map(v => (
            <button key={v} className="sm" onClick={() => setBody(b => b + v)}
              style={{ fontSize: 13, fontFamily: 'monospace', padding: '2px 8px' }}>
              {v}
            </button>
          ))}
        </div>
      </div>
      <div className="field">
        <label>Subject line</label>
        <input value={subject} onChange={e => setSubject(e.target.value)} />
      </div>
      <div className="field">
        <label>Email body</label>
        <textarea value={body} onChange={e => setBody(e.target.value)} rows={10}
          style={{ fontFamily: 'monospace', fontSize: 13 }} />
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button className="primary" onClick={save}>{savedMsg ? '✓ Saved' : 'Save template'}</button>
        <button onClick={reset} style={{ fontSize: 13, color: 'var(--text3)' }}>Reset to default</button>
      </div>
    </div>
  )
}

export default function Settings() {
  const { profile, role, logAction } = useAuth()
  const [newPassword, setNewPassword] = useState('')
  const [msg, setMsg] = useState('')
  const [users, setUsers] = useState([])
  const [userMsg, setUserMsg] = useState('')

  useEffect(() => { loadUsers() }, [])

  async function loadUsers() {
    const { data } = await supabase.from('user_profiles').select('*').order('name')
    setUsers(data || [])
  }

  async function updateUserRole(userId, newRole) {
    await supabase.from('user_profiles').update({ role: newRole }).eq('id', userId)
    if (logAction) await logAction('User role updated', 'user', userId, newRole, { new_role: newRole })
    setUserMsg('Role updated!')
    setTimeout(() => setUserMsg(''), 2500)
    loadUsers()
  }

  // Turning access off also ends their logins on every device (database trigger, STAFF_ACCESS_OFF.sql).
  async function setUserActive(u, active) {
    if (!active && !window.confirm(`Turn off ${u.name}'s access?\n\nThey'll be logged out of AuditPM on all their devices and can't log in until you tick Active again.`)) return
    const { error } = await supabase.from('user_profiles').update({ active }).eq('id', u.id)
    if (error) { failed("Couldn't change access", error.message); return }
    if (logAction) await logAction(active ? 'User access turned on' : 'User access turned off', 'user', u.id, u.name, {})
    setUserMsg(active ? `${u.name} can log in again.` : `${u.name}'s access is turned off.`)
    setTimeout(() => setUserMsg(''), 2500)
    loadUsers()
  }
  const [firmName, setFirmName] = useState(localStorage.getItem('firm_name') || '')
  const [logoUrl, setLogoUrl] = useState(localStorage.getItem('firm_logo_url') || '')
  const [logoUploading, setLogoUploading] = useState(false)
  const [sacLogoUrl, setSacLogoUrl] = useState(localStorage.getItem('sac_logo_url') || '')
  const [sacUploading, setSacUploading] = useState(false)
  const [stampUrl, setStampUrl] = useState(localStorage.getItem('firm_stamp_url') || '')
  const [stampUploading, setStampUploading] = useState(false)
  const [retentionYears, setRetentionYears] = useState('')
  const [retentionNote, setRetentionNote] = useState('')
  const [cpdCycleStart, setCpdCycleStart] = useState('')
  const [cpdCycleExpiry, setCpdCycleExpiry] = useState('')

  useEffect(() => {
    supabase.from('firm_settings')
      .select('retention_years, retention_policy_note, firm_name, firm_address, firm_phone, firm_email, firm_logo_base64, sac_logo_base64, stamp_base64, cpd_cycle_start, cpd_cycle_expiry')
      .eq('id', 1).maybeSingle()
      .then(({ data }) => {
        if (!data) return
        setRetentionYears(data.retention_years ?? ''); setRetentionNote(data.retention_policy_note || '')
        // Firm details + logos are FIRM-WIDE (the DB is the source of truth). Load them so EVERY user sees
        // what Admin saved — localStorage is per-browser, so a teammate's device would otherwise look empty.
        if (data.firm_name) setFirmName(data.firm_name)
        if (data.firm_address) setFirmAddress(data.firm_address)
        if (data.firm_email) setFirmEmail(data.firm_email)
        if (data.firm_phone) setFirmPhone(data.firm_phone)
        if (data.firm_logo_base64) setLogoUrl(data.firm_logo_base64)
        if (data.sac_logo_base64) setSacLogoUrl(data.sac_logo_base64)
        if (data.stamp_base64) setStampUrl(data.stamp_base64)
        if (data.cpd_cycle_start) setCpdCycleStart(data.cpd_cycle_start)
        if (data.cpd_cycle_expiry) setCpdCycleExpiry(data.cpd_cycle_expiry)
      })
  }, [])

  async function saveRetention() {
    await supabase.from('firm_settings').upsert({ id: 1, retention_years: retentionYears === '' ? null : parseInt(retentionYears) || null, retention_policy_note: retentionNote || null, updated_at: new Date().toISOString() })
    setMsg('Retention policy saved!'); setTimeout(() => setMsg(''), 2000)
  }

  async function saveCpdCycle() {
    await supabase.from('firm_settings').upsert({ id: 1, cpd_cycle_start: cpdCycleStart || null, cpd_cycle_expiry: cpdCycleExpiry || null, updated_at: new Date().toISOString() })
    setMsg('CPD accreditation cycle saved!'); setTimeout(() => setMsg(''), 2000)
  }
  const [firmAddress, setFirmAddress] = useState(localStorage.getItem('firm_address') || '')
  const [firmEmail, setFirmEmail] = useState(localStorage.getItem('firm_email') || '')
  const [firmPhone, setFirmPhone] = useState(localStorage.getItem('firm_phone') || '')
  const [userInitials, setUserInitials] = useState(localStorage.getItem('user_initials') || '')
  const [savingInitials, setSavingInitials] = useState(false)
  const [openTemplate, setOpenTemplate] = useState(null)
  // Each user's quotation initials come from their OWN profile (DB), set via the set_my_initials RPC
  // (direct user_profiles writes are Admin-only). The quote ref then reads the sender's initials.
  useEffect(() => { if (profile?.initials) setUserInitials(profile.initials) }, [profile?.initials])
  async function saveInitials() {
    setSavingInitials(true)
    const { error } = await supabase.rpc('set_my_initials', { p_initials: userInitials })
    setSavingInitials(false)
    if (error) { setMsg('Could not save initials: ' + error.message); setTimeout(() => setMsg(''), 3000); return }
    localStorage.setItem('user_initials', (userInitials || '').toUpperCase())
    setMsg('Your initials saved!'); setTimeout(() => setMsg(''), 2000)
  }

  async function changePassword() {
    const { error } = await supabase.auth.updateUser({ password: newPassword })
    setMsg(error ? error.message : 'Password updated.')
    setNewPassword('')
  }

  async function uploadLogo(file) {
    if (!file) return
    if (!file.type.startsWith('image/')) { toast('Choose an image file', 'warning', { detail: 'PNG or JPG' }); return }
    if (file.size > 2 * 1024 * 1024) { toast('Image too large', 'warning', { detail: 'Keep it under 2 MB' }); return }
    setLogoUploading(true)
    try {
      // Convert to base64 and store in localStorage (simple approach, no storage bucket needed)
      const reader = new FileReader()
      reader.onload = async (e) => {
        const dataUrl = e.target.result
        localStorage.setItem('firm_logo_url', dataUrl)
        setLogoUrl(dataUrl)
        // Save to Supabase so external pages (client/auditor devices) can load it
        await supabase.from('firm_settings').upsert({ id: 1, firm_logo_base64: dataUrl, updated_at: new Date().toISOString() })
        setLogoUploading(false)
        setMsg('Logo saved!')
        setTimeout(() => setMsg(''), 2000)
      }
      reader.readAsDataURL(file)
    } catch (e) {
      failed('Upload failed', e.message)
      setLogoUploading(false)
    }
  }

  async function removeLogo() {
    localStorage.removeItem('firm_logo_url')
    setLogoUrl('')
    await supabase.from('firm_settings').upsert({ id: 1, firm_logo_base64: null, updated_at: new Date().toISOString() })
  }

  async function uploadSacLogo(file) {
    if (!file) return
    if (!file.type.startsWith('image/')) { toast('Choose an image file', 'warning', { detail: 'PNG or JPG' }); return }
    if (file.size > 2 * 1024 * 1024) { toast('Image too large', 'warning', { detail: 'Keep it under 2 MB' }); return }
    setSacUploading(true)
    const reader = new FileReader()
    reader.onload = async (e) => {
      const dataUrl = e.target.result
      localStorage.setItem('sac_logo_url', dataUrl)
      setSacLogoUrl(dataUrl)
      await supabase.from('firm_settings').upsert({ id: 1, sac_logo_base64: dataUrl, updated_at: new Date().toISOString() })
      setSacUploading(false); setMsg('SAC logo saved!'); setTimeout(() => setMsg(''), 2000)
    }
    reader.readAsDataURL(file)
  }
  async function removeSacLogo() {
    localStorage.removeItem('sac_logo_url')
    setSacLogoUrl('')
    await supabase.from('firm_settings').upsert({ id: 1, sac_logo_base64: null, updated_at: new Date().toISOString() })
  }

  async function uploadStamp(file) {
    if (!file) return
    if (!file.type.startsWith('image/')) { toast('Choose an image file', 'warning', { detail: 'A PNG with a transparent background works best' }); return }
    if (file.size > 2 * 1024 * 1024) { toast('Image too large', 'warning', { detail: 'Keep it under 2 MB' }); return }
    setStampUploading(true)
    const reader = new FileReader()
    reader.onload = async (e) => {
      const dataUrl = e.target.result
      localStorage.setItem('firm_stamp_url', dataUrl)
      setStampUrl(dataUrl)
      await supabase.from('firm_settings').upsert({ id: 1, stamp_base64: dataUrl, updated_at: new Date().toISOString() })
      setStampUploading(false); setMsg('Company stamp saved!'); setTimeout(() => setMsg(''), 2000)
    }
    reader.readAsDataURL(file)
  }
  async function removeStamp() {
    localStorage.removeItem('firm_stamp_url')
    setStampUrl('')
    await supabase.from('firm_settings').upsert({ id: 1, stamp_base64: null, updated_at: new Date().toISOString() })
  }

  async function saveFirmDetails() {
    localStorage.setItem('firm_name', firmName)
    localStorage.setItem('firm_address', firmAddress)
    localStorage.setItem('firm_email', firmEmail)
    localStorage.setItem('firm_phone', firmPhone)
    // Save to Supabase so all external pages show correct details
    await supabase.from('firm_settings').upsert({
      id: 1,
      firm_name: firmName,
      firm_address: firmAddress,
      firm_phone: firmPhone,
      firm_email: firmEmail,
      updated_at: new Date().toISOString(),
    })
    setMsg('Firm details saved!')
    setTimeout(() => setMsg(''), 2000)
  }

  return (
    <div style={{ maxWidth: 640 }}>
      <div className="page-header"><h1>Settings</h1></div>

      {msg && <div className="notice notice-info" style={{ marginBottom: 16 }}>{msg}</div>}

      {/* Firm details */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h2 className="card-title">Firm details</h2>
        <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
          Used on audit plans, quotations and emails.
        </p>
        <div className="grid-2">
          <div className="field"><label>Firm name</label>
            <input value={firmName} onChange={e => setFirmName(e.target.value)} placeholder="CCIS Singapore Pte Ltd" /></div>
          <div className="field"><label>Email address</label>
            <input type="email" value={firmEmail} onChange={e => setFirmEmail(e.target.value)} placeholder="query@ccis.com.sg" /></div>
        </div>
        <div className="grid-2">
          <div className="field"><label>Phone</label>
            <input value={firmPhone} onChange={e => setFirmPhone(e.target.value)} placeholder="(65) 6376 0550" /></div>
          <div></div>
        </div>
        <div className="field"><label>Address</label>
          <textarea value={firmAddress} onChange={e => setFirmAddress(e.target.value)}
            placeholder="16 Jalan Kilang Timor #08-03, Redhill Forum Singapore 159308" rows={2} /></div>
        <h2 className="card-title">Your quotation initials</h2>
        <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 14 }}>
          Your abbreviation appears in the reference of every quotation you send (e.g. <strong>pm/1501-26/{userInitials || 'WT'}</strong>) so the team can see at a glance who issued it. Each user sets their own.
        </p>
        <div className="field" style={{ maxWidth: 280 }}><label>Your initials</label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input value={userInitials} onChange={e => setUserInitials(e.target.value.toUpperCase())} placeholder="e.g. WT" maxLength={4} style={{ maxWidth: 120 }} />
            <button className="sm" disabled={savingInitials || !userInitials.trim()} onClick={saveInitials}>{savingInitials ? 'Saving…' : 'Save'}</button>
          </div>
        </div>
        <h2 className="card-title">User management</h2>
        <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 12 }}>
          Assign roles to each user. Users must first be created in Supabase Auth dashboard, then added here.
        </p>
        {userMsg && <div style={{ background: 'var(--brand-light)', color: 'var(--brand-dark)', padding: '8px 12px', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>{userMsg}</div>}
        <div style={{ marginBottom: 16 }}>
          <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 500, color: 'var(--text2)' }}>Name</th>
                <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 500, color: 'var(--text2)' }}>Decision maker</th>
                <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 500, color: 'var(--text2)' }}>Report handler</th>
                <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 500, color: 'var(--text2)' }}>Role</th>
                <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 500, color: 'var(--text2)' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && (
                <tr><td colSpan={5} style={{ padding: '12px 8px', color: 'var(--text3)', fontStyle: 'italic' }}>
                  No users yet. Create users in Supabase Auth dashboard, then insert their profiles via SQL.
                </td></tr>
              )}
              {users.map(u => (
                <tr key={u.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '8px' }}><strong>{u.name}</strong></td>
                  <td style={{ padding: '8px' }}>
                    <input type="checkbox" checked={u.is_decision_maker || false}
                      title="Can record audit decisions / approve reports (ISO 17021)"
                      onChange={async e => {
                        await supabase.from('user_profiles').update({ is_decision_maker: e.target.checked }).eq('id', u.id)
                        loadUsers()
                      }} style={{ accentColor: '#006363', width: 'auto' }} />
                  </td>
                  <td style={{ padding: '8px' }}>
                    <input type="checkbox" checked={u.can_handle_reports || false}
                      title="Can be routed an approved report to send to the client + record the WSHC submission"
                      onChange={async e => {
                        await supabase.from('user_profiles').update({ can_handle_reports: e.target.checked }).eq('id', u.id)
                        loadUsers()
                      }} style={{ accentColor: '#006363', width: 'auto' }} />
                  </td>
                  <td style={{ padding: '8px' }}>
                    <select value={u.role} onChange={e => updateUserRole(u.id, e.target.value)}
                      style={{ width: 'auto', fontSize: 13, padding: '4px 8px' }}>
                      {['Sales Manager','Operations Manager','Quality Manager (Primary)','Quality Manager (Delegate)','Document Controller','Administrative Assistant','Admin','Auditor'].map(r => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </td>
                  <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>
                    <input type="checkbox" checked={u.active !== false} disabled={u.id === profile?.id}
                      title={u.id === profile?.id ? "You can't turn off your own access" : 'Untick to turn off this person’s access to AuditPM'}
                      onChange={e => setUserActive(u, e.target.checked)}
                      style={{ accentColor: '#006363', width: 'auto', marginRight: 6, verticalAlign: 'middle' }} />
                    <span style={{ fontSize: 13, padding: '2px 8px', borderRadius: 12, background: u.active ? '#EAF3DE' : '#f0f0f0', color: u.active ? '#27500a' : '#888' }}>
                      {u.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ background: 'var(--bg2)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
          To add a new user: go to Supabase dashboard → Authentication → Users → Invite user. Then run:<br />
          <code style={{ fontFamily: 'monospace', background: 'var(--bg3)', padding: '2px 6px', borderRadius: 4 }}>
            insert into user_profiles (id, name, role) values ('their-uuid', 'Their Name', 'Their Role');
          </code>
        </div>

        <h2 className="card-title">Company logo</h2>
        <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 12 }}>
          Upload your company logo (PNG recommended, max 2MB). It will appear on quotations, audit plans, and client-facing pages.
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 14 }}>
          {logoUrl ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <img src={logoUrl} alt="Company logo" style={{ maxHeight: 60, maxWidth: 180, objectFit: 'contain', border: '1px solid var(--border)', borderRadius: 6, padding: 4, background: '#fff' }} />
              <button className="sm danger" onClick={removeLogo}>Remove</button>
            </div>
          ) : (
            <div style={{ fontSize: 13, color: 'var(--text3)', fontStyle: 'italic' }}>No logo uploaded</div>
          )}
        </div>
        <label style={{ display: 'inline-block', padding: '7px 14px', borderRadius: 'var(--radius)', border: '1px solid var(--border2)', background: 'var(--bg)', cursor: 'pointer', fontSize: 13, color: 'var(--text)', marginBottom: 16 }}>
          {logoUploading ? 'Uploading…' : '📁 Choose logo file'}
          <input type="file" accept="image/*" onChange={e => uploadLogo(e.target.files[0])} style={{ display: 'none' }} />
        </label>

        <h2 className="card-title">SAC logo</h2>
        <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 12 }}>
          Upload the SAC accreditation logo (PNG recommended, max 2MB). It appears on the right side of the audit report header.
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 14 }}>
          {sacLogoUrl ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <img src={sacLogoUrl} alt="SAC logo" style={{ maxHeight: 60, maxWidth: 180, objectFit: 'contain', border: '1px solid var(--border)', borderRadius: 6, padding: 4, background: '#fff' }} />
              <button className="sm danger" onClick={removeSacLogo}>Remove</button>
            </div>
          ) : (
            <div style={{ fontSize: 13, color: 'var(--text3)', fontStyle: 'italic' }}>No SAC logo uploaded</div>
          )}
        </div>
        <label style={{ display: 'inline-block', padding: '7px 14px', borderRadius: 'var(--radius)', border: '1px solid var(--border2)', background: 'var(--bg)', cursor: 'pointer', fontSize: 13, color: 'var(--text)', marginBottom: 16 }}>
          {sacUploading ? 'Uploading…' : '📁 Choose SAC logo file'}
          <input type="file" accept="image/*" onChange={e => uploadSacLogo(e.target.files[0])} style={{ display: 'none' }} />
        </label>

        <h2 className="card-title">Company stamp</h2>
        <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 12 }}>
          Upload the auditing organisation's company stamp (PNG with transparent background recommended, max 2MB). It appears in the “issued by” block of the audit report.
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 14 }}>
          {stampUrl ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <img src={stampUrl} alt="Company stamp" style={{ maxHeight: 70, maxWidth: 180, objectFit: 'contain', border: '1px solid var(--border)', borderRadius: 6, padding: 4, background: '#fff' }} />
              <button className="sm danger" onClick={removeStamp}>Remove</button>
            </div>
          ) : (
            <div style={{ fontSize: 13, color: 'var(--text3)', fontStyle: 'italic' }}>No company stamp uploaded</div>
          )}
        </div>
        <label style={{ display: 'inline-block', padding: '7px 14px', borderRadius: 'var(--radius)', border: '1px solid var(--border2)', background: 'var(--bg)', cursor: 'pointer', fontSize: 13, color: 'var(--text)', marginBottom: 16 }}>
          {stampUploading ? 'Uploading…' : '📁 Choose stamp file'}
          <input type="file" accept="image/*" onChange={e => uploadStamp(e.target.files[0])} style={{ display: 'none' }} />
        </label>
        <br />
        <button className="primary" onClick={saveFirmDetails}>Save firm details</button>
      </div>

      {/* Records & retention */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h2 className="card-title">Records &amp; retention</h2>
        <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 12 }}>
          Defines how long audit records are retained (per the firm's documented policy and scheme requirements).
          Records are kept and not purged automatically by the system.
        </p>
        <div className="grid-2">
          <div className="field"><label>Retention period (years)</label>
            <input type="number" min="1" value={retentionYears} onChange={e => setRetentionYears(e.target.value)} placeholder="e.g. 3" /></div>
        </div>
        <div className="field"><label>Retention policy note</label>
          <textarea rows={2} value={retentionNote} onChange={e => setRetentionNote(e.target.value)} placeholder="e.g. Audit records retained for 3 years from date of audit (bizSAFE validity); thereafter archived/disposed per policy." /></div>
        <button className="primary" onClick={saveRetention}>Save retention policy</button>

        <div style={{ marginTop: 16, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Records register (where records are kept)</div>
          <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
            <thead><tr><th style={{ textAlign: 'left', padding: '4px 6px' }}>Record</th><th style={{ textAlign: 'left', padding: '4px 6px' }}>Stored in</th><th style={{ textAlign: 'left', padding: '4px 6px' }}>Notes</th></tr></thead>
            <tbody style={{ color: 'var(--text2)' }}>
              {[
                ['Audit reports / packages', 'audit_report_snapshots (DB)', 'Versioned, immutable snapshot per submission'],
                ['Report acknowledgements', 'report_ack_responses (DB)', 'Immutable; timestamp, IP, user-agent, version'],
                ['Evidence files / packs', 'Supabase Storage (audit-evidence)', 'Access-controlled bucket; referenced by annex'],
                ['Client records', 'clients (DB)', 'Includes bizSAFE expiry (source of truth)'],
                ['Complaints', 'complaints (DB)', 'Full lifecycle record'],
                ['Impartiality / COI', 'impartiality_register, auditor_coi_responses, coi_decisions (DB)', 'Threats, mitigations, decisions'],
                ['Personnel / competence', 'auditors, auditor_cpd, auditor_audit_log, competency_events (DB)', 'CT17 maintenance + competence'],
                ['Audit trail', 'audit_trail (DB)', 'System action log'],
              ].map(([a, b, c], i) => (
                <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '4px 6px', fontWeight: 500, color: 'var(--text)' }}>{a}</td>
                  <td style={{ padding: '4px 6px' }}>{b}</td>
                  <td style={{ padding: '4px 6px' }}>{c}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 10 }}>
            <strong>Confidentiality:</strong> all records are access-restricted to authenticated firm users (role-based);
            client-facing links (report acknowledgement, plan, COI, quotation signing) are single-purpose tokenised pages
            that expose only the relevant record and cannot be edited after submission.
          </div>
        </div>
      </div>

      {/* CPD accreditation cycle */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h2 className="card-title">AO Scheme accreditation (CPD cycle)</h2>
        <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 12 }}>
          The firm's SAC Auditing Organisation accreditation period. This is a single, company-wide cycle (not
          per-auditor) that anchors the 4-year CPD requirement — each non-WSHO auditor must record at least 10
          accredited (SDU-point) hours and 10 non-accredited hours of training every cycle-year, checked under
          Personnel → Training. Auditors who hold a valid WSH Officer registration are exempt.
        </p>
        <div className="grid-2">
          <div className="field"><label>Cycle start date</label>
            <input type="date" value={cpdCycleStart} onChange={e => setCpdCycleStart(e.target.value)} /></div>
          <div className="field"><label>Cycle expiry date</label>
            <input type="date" value={cpdCycleExpiry} onChange={e => setCpdCycleExpiry(e.target.value)} /></div>
        </div>
        <button className="primary" onClick={saveCpdCycle}>Save accreditation cycle</button>
      </div>

      {/* Email templates */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h2 className="card-title">Email templates</h2>
        <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
          Customise the emails that get drafted automatically. Use the variable buttons to insert dynamic values like client name or form links.
        </p>
        {Object.entries(TEMPLATE_LABELS).map(([key, info]) => (
          <div key={key} style={{ marginBottom: 8 }}>
            <div
              onClick={() => setOpenTemplate(openTemplate === key ? null : key)}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: 'var(--bg2)', borderRadius: 'var(--radius)', cursor: 'pointer', border: openTemplate === key ? '0.5px solid var(--accent)' : '0.5px solid var(--border)' }}
            >
              <div>
                <div style={{ fontWeight: 500, fontSize: 14 }}>{info.label}</div>
                <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 2 }}>{info.desc}</div>
              </div>
              <span style={{ fontSize: 18, color: 'var(--text3)', marginLeft: 12 }}>{openTemplate === key ? '↑' : '↓'}</span>
            </div>
            {openTemplate === key && (
              <div style={{ padding: '0 4px' }}>
                <TemplateEditor templateKey={key} onSaved={() => {}} />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Password */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h2 className="card-title">Change password</h2>
        <div className="field"><label>New password</label>
          <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} /></div>
        <button className="primary" onClick={changePassword} disabled={newPassword.length < 6}>Update password</button>
      </div>

      <div className="card">
        <h2 className="card-title">About</h2>
        <p style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.7 }}>
          AuditPM · CT 17 man-day calculations based on SAC CT 17 (24 July 2024). Renewal reminders run daily via Supabase Edge Functions + Resend.
        </p>
      </div>
    </div>
  )
}